/**
 * Checks if a URL is a YouTube video URL
 * @param {string} url - The video URL to check
 * @returns {boolean} - True if the URL is a YouTube URL
 */
export const isYouTubeUrl = (url) => {
  if (!url) return false;
  
  const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/;
  return youtubeRegex.test(url);
};

/**
 * Extracts video ID from YouTube URL
 * @param {string} url - The YouTube video URL
 * @returns {string|null} - The video ID or null if invalid
 */
export const getYouTubeVideoId = (url) => {
  if (!url || !isYouTubeUrl(url)) return null;

  // Extract video ID from various YouTube URL formats
  let videoId = null;
  
  // Format: https://www.youtube.com/watch?v=VIDEO_ID
  const watchMatch = url.match(/(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]+)/);
  if (watchMatch) {
    videoId = watchMatch[1];
  }
  
  // Format: https://youtu.be/VIDEO_ID
  const shortMatch = url.match(/(?:youtu\.be\/)([a-zA-Z0-9_-]+)/);
  if (shortMatch) {
    videoId = shortMatch[1];
  }
  
  // Format: https://www.youtube.com/embed/VIDEO_ID
  const embedMatch = url.match(/(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/);
  if (embedMatch) {
    videoId = embedMatch[1];
  }
  
  return videoId;
};

/**
 * Converts a YouTube URL to an embed URL
 * @param {string} url - The YouTube video URL
 * @returns {string|null} - The embed URL or null if invalid
 */
export const getYouTubeEmbedUrl = (url) => {
  const videoId = getYouTubeVideoId(url);
  if (!videoId) return null;
  return `https://www.youtube.com/embed/${videoId}`;
};

/**
 * Gets YouTube video thumbnail URL
 * @param {string} url - The YouTube video URL
 * @param {string} quality - Thumbnail quality: 'default', 'medium', 'high', 'maxres'
 * @returns {string|null} - The thumbnail URL or null if invalid
 */
export const getYouTubeThumbnail = (url, quality = 'high') => {
  const videoId = getYouTubeVideoId(url);
  if (!videoId) return null;
  return `https://img.youtube.com/vi/${videoId}/${quality}default.jpg`;
};

